import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiErrorMessage } from '../api/client';
import * as analyticsApi from '../api/analyticsApi';
import {
  Card,
  EmptyState,
  ErrorBanner,
  Pagination,
  Skeleton,
  TableSkeleton,
} from '../components/ui';
import { CartUsersModal } from '../components/CartUsersModal';
import { ConvertedOrdersModal } from '../components/ConvertedOrdersModal';
import { FormulationUsersModal } from '../components/FormulationUsersModal';
import { IconAlert, IconRefresh, IconX } from '../components/icons';
import { useTheme } from '../context/ThemeContext';
import {
  CohortPeriod,
  FunnelAnalytics,
  FunnelStage,
  FunnelStageUsers,
  Granularity,
  OtpStats,
  RepeatRateAnalytics,
} from '../types/analytics';
import { chartTheme } from '../utils/chartTheme';
import { formatDate, formatDateShort } from '../utils/format';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Below this many people, a percentage is noise — one person changes it by tens
 * of points. Anything smaller gets an explicit "don't trust this" warning rather
 * than a confident-looking number.
 */
const SMALL_SAMPLE = 10;
const SMALL_COHORT = 5;

/* ---------- date helpers ---------- */

function isoDay(d: Date): string {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDay(d);
}

const TODAY = () => isoDay(new Date());

/** Preset windows, so nobody has to type a date to answer "how are we doing?". */
const FUNNEL_PRESETS = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
  { value: '365d', label: 'Last 12 months', days: 365 },
] as const;

const COHORT_PRESETS = [
  { value: '365d', label: 'Last 12 months', days: 365 },
  { value: '730d', label: 'Last 2 years', days: 730 },
  { value: '1095d', label: 'Last 3 years', days: 1095 },
] as const;

/**
 * Render a gateway period key. `long` is for tooltips and table cells; the short
 * form keeps chart axes from crowding.
 *   day   2026-08-08 → "08 Aug"  / "08 Aug 2026"
 *   week  2026-08-03 → "08 Aug"  / "Week of 03 Aug 2026"
 *   month 2026-08    → "Aug"     / "Aug 2026"
 */
function periodLabel(period: string, granularity: Granularity, long = false): string {
  if (granularity === 'month') {
    const [year, month] = period.split('-');
    const name = MONTHS[Number(month) - 1];
    if (!name) return period;
    return long ? `${name} ${year}` : name;
  }
  if (granularity === 'week') {
    return long ? `Week of ${formatDate(period)}` : formatDateShort(period);
  }
  return long ? formatDate(period) : formatDateShort(period);
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * "42.9%", with an empty denominator reading 0.0% rather than NaN. Byte-for-byte
 * the gateway's own formatting, so a rate computed here is indistinguishable
 * from one that arrived on the response.
 */
function ratePct(part: number, whole: number): string {
  if (!whole) return '0.0%';
  return `${((part / whole) * 100).toFixed(1)}%`;
}

/* ---------- small shared pieces ---------- */

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex shrink-0 overflow-hidden rounded-lg border border-edge text-xs font-medium">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 transition-colors ${
            value === o.value ? 'bg-gold text-[#1a1408]' : 'text-sub hover:text-ink'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Preset dropdown that falls back to two date inputs on "Custom range". */
function RangePicker({
  presets,
  preset,
  from,
  to,
  onPreset,
  onFrom,
  onTo,
}: {
  presets: readonly { value: string; label: string; days: number }[];
  preset: string;
  from: string;
  to: string;
  onPreset: (v: string) => void;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        className="input !w-auto !py-1.5 !text-xs"
        value={preset}
        onChange={(e) => onPreset(e.target.value)}
        aria-label="Date range"
      >
        {presets.map((p) => (
          <option key={p.value} value={p.value}>
            {p.label}
          </option>
        ))}
        <option value="custom">Custom range…</option>
      </select>
      {preset === 'custom' && (
        <>
          <input
            type="date"
            className="input !w-auto !py-1.5 !text-xs"
            value={from}
            max={to}
            onChange={(e) => onFrom(e.target.value)}
            aria-label="From date"
          />
          <span className="text-xs text-sub">to</span>
          <input
            type="date"
            className="input !w-auto !py-1.5 !text-xs"
            value={to}
            min={from}
            onChange={(e) => onTo(e.target.value)}
            aria-label="To date"
          />
        </>
      )}
    </div>
  );
}

/** Amber caution strip — used only for "this number can mislead you" warnings. */
function Caution({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-xs text-warn">
      <IconAlert className="mt-0.5 h-4 w-4 shrink-0" />
      <p>{children}</p>
    </div>
  );
}

function SectionHeading({
  title,
  blurb,
  children,
}: {
  title: string;
  blurb: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="mt-0.5 max-w-xl text-xs text-sub">{blurb}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">{children}</div>
    </div>
  );
}

/* ---------- OTP login health ---------- */

/**
 * Colour the verify rate by how worried to be. Below 60% something is wrong
 * with delivery or with the code screen; above 80% is normal for SMS OTP, where
 * a slice of people always abandon after requesting a code.
 */
function otpRateTone(rate: number): string {
  if (rate > 80) return 'text-ok';
  if (rate > 60) return 'text-warn';
  return 'text-bad';
}

function OtpStat({
  label,
  value,
  tone = '',
}: {
  label: string;
  value: string | number;
  tone?: string;
}) {
  return (
    <div className="rounded-xl border border-edge px-4 py-3">
      <p className="text-xs text-sub">{label}</p>
      <p className={`mt-1 text-xl font-semibold tabular-nums ${tone}`}>{value}</p>
    </div>
  );
}

/* ---------- funnel ---------- */

interface Stage {
  key: FunnelStage;
  label: string;
  /** What this bar counts, in words — the business team should not have to guess. */
  meaning: string;
  count: number;
  color: string;
  /** Conversion from the previous stage — absent on the first stage. */
  stepPct?: string;
  stepLabel?: string;
}

function FunnelStages({
  stages,
  selected,
  onSelect,
}: {
  stages: Stage[];
  selected: FunnelStage | null;
  onSelect: (s: FunnelStage) => void;
}) {
  // Bars scale against the top of the funnel so the drop-off is the shape you
  // read, not just the numbers. A non-zero stage keeps a 2% sliver so it never
  // disappears entirely.
  const top = stages[0]?.count ?? 0;
  const width = (n: number) => (top <= 0 ? 0 : Math.max((n / top) * 100, n > 0 ? 2 : 0));
  const ofTotal = (n: number) => (top <= 0 ? '0%' : `${((n / top) * 100).toFixed(0)}%`);

  return (
    <div>
      {stages.map((s, i) => (
        <div key={s.key}>
          {i > 0 && (
            <div className="flex items-baseline gap-2 py-2 pl-1 text-xs">
              <span className="text-sub" aria-hidden="true">
                ↓
              </span>
              <span className="font-semibold" style={{ color: s.color }}>
                {s.stepPct}
              </span>
              <span className="text-sub">{s.stepLabel}</span>
            </div>
          )}
          <button
            type="button"
            onClick={() => onSelect(s.key)}
            aria-pressed={selected === s.key}
            className={`-mx-2 block w-[calc(100%+1rem)] rounded-lg px-2 py-2 text-left transition-colors ${
              selected === s.key ? 'bg-edge/50 ring-1 ring-inset ring-edge' : 'hover:bg-edge/30'
            }`}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium">{s.label}</span>
              <span className="shrink-0 text-lg font-semibold tabular-nums">
                {s.count.toLocaleString('en-IN')}
                {i > 0 && (
                  <span className="ml-1.5 text-xs font-normal text-sub">
                    ({ofTotal(s.count)} of all signups)
                  </span>
                )}
              </span>
            </div>
            <p className="mb-1.5 mt-0.5 text-xs text-sub">{s.meaning}</p>
            <div className="h-3 overflow-hidden rounded-full bg-edge/60">
              <div
                className="h-full rounded-full transition-[width] duration-500"
                style={{ width: `${width(s.count)}%`, backgroundColor: s.color }}
              />
            </div>
            <p className="mt-1 text-[11px] text-sub/70">
              {selected === s.key ? 'Click to hide the list' : 'Click to see who these people are'}
            </p>
          </button>
        </div>
      ))}
    </div>
  );
}

/* ---------- formulation funnel ---------- */

interface FormulationStage {
  label: string;
  /** What this bar counts, in words. */
  meaning: string;
  count: number;
  color: string;
  /** Conversion from the stage above — absent on the first. */
  stepPct?: string;
  /** How many were lost between the stage above and this one. */
  lost?: number;
  lostLabel?: string;
  /** Opens the list behind this bar. Bars without one stay plain text: a bar
   *  that looks clickable but isn't is worse than one that plainly isn't. */
  onOpen?: () => void;
  /** What that list contains. Each bar opens a different one, so the prompt
   *  under it has to say which. */
  openHint?: string;
}

/** The same shape as the user-journey funnel; only the stages that can be
 *  drilled into are rendered as buttons. */
function FormulationFunnelStages({ stages }: { stages: FormulationStage[] }) {
  const top = stages[0]?.count ?? 0;
  const width = (n: number) => (top <= 0 ? 0 : Math.max((n / top) * 100, n > 0 ? 2 : 0));
  const ofTotal = (n: number) => (top <= 0 ? '0%' : `${((n / top) * 100).toFixed(0)}%`);

  return (
    <div>
      {stages.map((s, i) => (
        <div key={s.label}>
          {i > 0 && (
            <div className="flex flex-wrap items-baseline gap-2 py-2 pl-1 text-xs">
              <span className="text-sub" aria-hidden="true">
                ↓
              </span>
              <span className="font-semibold" style={{ color: s.color }}>
                {s.stepPct}
              </span>
              <span className="text-sub">of the step above got this far</span>
              {s.lost !== undefined && s.lost > 0 && (
                <span className="text-warn">
                  — {s.lost.toLocaleString('en-IN')} {s.lostLabel}
                </span>
              )}
            </div>
          )}
          <div className="-mx-2 rounded-lg px-2 py-2">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium">{s.label}</span>
              <span className="shrink-0 text-lg font-semibold tabular-nums">
                {s.onOpen ? (
                  <button
                    type="button"
                    onClick={s.onOpen}
                    className="rounded text-gold underline decoration-dotted underline-offset-4 transition-colors hover:text-ink"
                    title="See who these people are"
                  >
                    {s.count.toLocaleString('en-IN')}
                  </button>
                ) : (
                  s.count.toLocaleString('en-IN')
                )}
                {i > 0 && (
                  <span className="ml-1.5 text-xs font-normal text-sub">
                    ({ofTotal(s.count)} of all formulas)
                  </span>
                )}
              </span>
            </div>
            <p className="mb-1.5 mt-0.5 text-xs text-sub">{s.meaning}</p>
            <div className="h-3 overflow-hidden rounded-full bg-edge/60">
              <div
                className="h-full rounded-full transition-[width] duration-500"
                style={{ width: `${width(s.count)}%`, backgroundColor: s.color }}
              />
            </div>
            {s.onOpen && s.openHint && (
              <p className="mt-1 text-[11px] text-sub/70">{s.openHint}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

const STAGE_TITLES: Record<FunnelStage, string> = {
  signups: 'Signed up',
  cart: 'Added to cart',
  order: 'Placed an order',
  repeat: 'Came back for more',
};

/** The people behind one funnel bar. */
function StageUsers({
  stage,
  data,
  loading,
  error,
  onPage,
  onClose,
  onRetry,
}: {
  stage: FunnelStage;
  data: FunnelStageUsers | null;
  loading: boolean;
  error: string;
  onPage: (p: number) => void;
  onClose: () => void;
  onRetry: () => void;
}) {
  const total = data?.pagination.total ?? 0;

  return (
    <Card
      title={`${STAGE_TITLES[stage]} — ${plural(total, 'person', 'people')}`}
      bodyClassName="!p-0"
      action={
        <button onClick={onClose} className="btn-ghost !p-1.5" aria-label="Close list">
          <IconX className="h-4 w-4" />
        </button>
      }
    >
      {error ? (
        <div className="p-5">
          <ErrorBanner message={error} onRetry={onRetry} />
        </div>
      ) : loading && !data ? (
        <TableSkeleton rows={5} />
      ) : !data || data.users.length === 0 ? (
        <EmptyState message="Nobody in this stage" hint="Try a wider date range" />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-edge">
                  <th className="th">Name</th>
                  <th className="th">Email or phone</th>
                  <th className="th">Signed up</th>
                  <th className="th">Orders so far</th>
                  <th className="th">Bag right now</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {data.users.map((u) => (
                  <tr key={u.id} className="transition-colors hover:bg-edge/20">
                    <td className="td">
                      <Link to={`/users/${u.id}`} className="font-medium text-gold hover:underline">
                        {u.display_name?.trim() || 'Unnamed'}
                      </Link>
                    </td>
                    <td className="td text-sub">{u.email || u.phone || '—'}</td>
                    <td className="td text-sub">{formatDate(u.created_at)}</td>
                    <td className="td tabular-nums">{u.orders_count}</td>
                    <td className="td text-sub">{u.has_cart_items ? 'Has items' : 'Empty'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={data.pagination.page} totalPages={data.pagination.pages} onPage={onPage} />
        </>
      )}
    </Card>
  );
}

/* ---------- page ---------- */

export default function AnalyticsPage() {
  const { isDark } = useTheme();
  const t = chartTheme(isDark);

  // One hue per funnel stage, reused by the trend chart so a colour means the
  // same thing in both places.
  const stageColors = {
    signups: t.gold,
    carts: t.status.shipped,
    orders: t.status.delivered,
    repeat: t.status.dispensing,
  };

  /* --- Section 1: user journey funnel --- */
  // 90 days weekly is the useful default: long enough for a trend to exist,
  // coarse enough that a low-volume day doesn't read as a collapse.
  const [funnelPreset, setFunnelPreset] = useState<string>('90d');
  const [customFrom, setCustomFrom] = useState(daysAgo(90));
  const [customTo, setCustomTo] = useState(TODAY());
  const [granularity, setGranularity] = useState<Granularity>('week');

  const { funnelFrom, funnelTo } = useMemo(() => {
    if (funnelPreset === 'custom') return { funnelFrom: customFrom, funnelTo: customTo };
    const days = FUNNEL_PRESETS.find((p) => p.value === funnelPreset)?.days ?? 90;
    return { funnelFrom: daysAgo(days), funnelTo: TODAY() };
  }, [funnelPreset, customFrom, customTo]);

  const [funnel, setFunnel] = useState<FunnelAnalytics | null>(null);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [funnelError, setFunnelError] = useState('');

  const loadFunnel = useCallback(async () => {
    setFunnelLoading(true);
    setFunnelError('');
    try {
      setFunnel(await analyticsApi.getFunnel({ from: funnelFrom, to: funnelTo, granularity }));
    } catch (err) {
      setFunnelError(apiErrorMessage(err));
    } finally {
      setFunnelLoading(false);
    }
  }, [funnelFrom, funnelTo, granularity]);

  useEffect(() => {
    loadFunnel();
  }, [loadFunnel]);

  /* --- Section 1b: funnel stage drill-down --- */
  const [stage, setStage] = useState<FunnelStage | null>(null);
  const [stagePage, setStagePage] = useState(1);
  const [stageUsers, setStageUsers] = useState<FunnelStageUsers | null>(null);
  const [stageLoading, setStageLoading] = useState(false);
  const [stageError, setStageError] = useState('');

  const loadStage = useCallback(async () => {
    if (!stage) return;
    setStageLoading(true);
    setStageError('');
    try {
      setStageUsers(
        await analyticsApi.getFunnelStageUsers({
          from: funnelFrom,
          to: funnelTo,
          stage,
          page: stagePage,
        }),
      );
    } catch (err) {
      setStageError(apiErrorMessage(err));
    } finally {
      setStageLoading(false);
    }
  }, [stage, stagePage, funnelFrom, funnelTo]);

  useEffect(() => {
    loadStage();
  }, [loadStage]);

  // Changing the date range invalidates whichever page of the list is open.
  useEffect(() => {
    setStagePage(1);
    setStageUsers(null);
  }, [funnelFrom, funnelTo]);

  const selectStage = (next: FunnelStage) => {
    // Clicking the open stage again closes it.
    setStage((cur) => (cur === next ? null : next));
    setStagePage(1);
    setStageUsers(null);
    setStageError('');
  };

  /* --- Section 1c: who is holding an unbought formula --- */
  // Its own dialog rather than an inline card: the list is not bound to the
  // date range the funnel is drawn from, and putting it in the page flow would
  // imply that it is.
  const [showCartUsers, setShowCartUsers] = useState(false);

  // The other two bars of the same funnel, on the same reasoning: neither list
  // is bound to the date range the bars are drawn from.
  const [showGenerators, setShowGenerators] = useState(false);
  const [showConverted, setShowConverted] = useState(false);

  /* --- Section 2: repeat purchase rate --- */
  const [cohortPeriod, setCohortPeriod] = useState<CohortPeriod>('monthly');
  const [repeatPreset, setRepeatPreset] = useState<string>('365d');
  const [repeatCustomFrom, setRepeatCustomFrom] = useState(daysAgo(365));
  const [repeatCustomTo, setRepeatCustomTo] = useState(TODAY());

  const { repeatFrom, repeatTo } = useMemo(() => {
    if (repeatPreset === 'custom') {
      return { repeatFrom: repeatCustomFrom, repeatTo: repeatCustomTo };
    }
    const days = COHORT_PRESETS.find((p) => p.value === repeatPreset)?.days ?? 365;
    return { repeatFrom: daysAgo(days), repeatTo: TODAY() };
  }, [repeatPreset, repeatCustomFrom, repeatCustomTo]);

  const [repeat, setRepeat] = useState<RepeatRateAnalytics | null>(null);
  const [repeatLoading, setRepeatLoading] = useState(true);
  const [repeatError, setRepeatError] = useState('');

  const loadRepeat = useCallback(async () => {
    setRepeatLoading(true);
    setRepeatError('');
    try {
      setRepeat(
        await analyticsApi.getRepeatRate({ period: cohortPeriod, from: repeatFrom, to: repeatTo }),
      );
    } catch (err) {
      setRepeatError(apiErrorMessage(err));
    } finally {
      setRepeatLoading(false);
    }
  }, [cohortPeriod, repeatFrom, repeatTo]);

  useEffect(() => {
    loadRepeat();
  }, [loadRepeat]);

  /* ---------- OTP login health ---------- */
  // Its own fixed 30-day window, so it deliberately ignores the range pickers
  // above — the auth service prunes spent OTP rows on a timer, which makes an
  // arbitrary historical range meaningless here.
  const [otp, setOtp] = useState<OtpStats | null>(null);
  const [otpLoading, setOtpLoading] = useState(true);
  // No error state on purpose: a failure hides the section. This is a
  // supplementary health panel, and an auth service that is down should not put
  // a red banner across a page whose main content loaded fine.
  const [otpFailed, setOtpFailed] = useState(false);

  const loadOtp = useCallback(async () => {
    setOtpLoading(true);
    try {
      setOtp(await analyticsApi.getOtpStats());
      setOtpFailed(false);
    } catch {
      setOtpFailed(true);
    } finally {
      setOtpLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOtp();
  }, [loadOtp]);

  // Recharts needs a stable array; format the axis label once here rather than
  // in a tick renderer that runs on every hover.
  const otpChartData = useMemo(
    () =>
      (otp?.daily ?? []).map((d) => ({
        ...d,
        label: formatDateShort(d.day),
      })),
    [otp],
  );

  const loading = funnelLoading || repeatLoading;

  const f = funnel?.funnel;
  const stages: Stage[] = funnel
    ? [
        {
          key: 'signups',
          label: 'Signed up',
          meaning: 'Created an account in this period',
          count: funnel.funnel.total_signups,
          color: stageColors.signups,
        },
        {
          key: 'cart',
          label: 'Added to cart',
          meaning: 'Put at least one product in their bag, or bought',
          count: funnel.funnel.added_to_cart,
          color: stageColors.carts,
          stepPct: funnel.conversion_rates.signup_to_cart,
          stepLabel: 'of the people above got this far',
        },
        {
          key: 'order',
          label: 'Placed an order',
          meaning: 'Completed and paid for at least one order',
          count: funnel.funnel.placed_order,
          color: stageColors.orders,
          stepPct: funnel.conversion_rates.cart_to_order,
          stepLabel: 'of the people above got this far',
        },
        {
          key: 'repeat',
          label: 'Came back for more',
          meaning: 'Placed a second order at some point',
          count: funnel.funnel.repeat_orderers,
          color: stageColors.repeat,
          stepPct: funnel.conversion_rates.order_to_repeat,
          stepLabel: 'of the people above got this far',
        },
      ]
    : [];

  // Formulation funnel — same colour language as the journey funnel above:
  // the first stage is gold, and each step down borrows the hue of the
  // equivalent step there, so "bag" and "bought" mean one colour on the page.
  const formulationColors = {
    generated: t.gold,
    carted: stageColors.carts,
    ordered: stageColors.orders,
  };

  const ff = funnel?.formulation_funnel;
  const formulationStages: FormulationStage[] = ff
    ? [
        {
          label: 'Generated a formula',
          meaning: 'Finished the quiz and was shown their formulation',
          count: ff.total_formulations_generated,
          color: formulationColors.generated,
          onOpen: () => setShowGenerators(true),
          openHint: 'Click the number to see who generated one, and whether they bought it',
        },
        {
          label: 'Added to cart',
          meaning: 'Put that formula in their bag, or bought it',
          count: ff.added_to_cart,
          color: formulationColors.carted,
          stepPct: ratePct(ff.added_to_cart, ff.total_formulations_generated),
          lost: ff.drop_off_at_cart,
          lostLabel: 'never left the reveal',
          onOpen: () => setShowCartUsers(true),
          openHint: 'Click the number to see who is holding an unbought formula',
        },
        {
          label: 'Placed an order',
          meaning: 'Paid for that formula',
          count: ff.placed_order,
          color: formulationColors.ordered,
          stepPct: ratePct(ff.placed_order, ff.added_to_cart),
          lost: ff.drop_off_at_checkout,
          lostLabel: 'never made it through checkout',
          onOpen: () => setShowConverted(true),
          openHint: 'Click the number to see the orders and the formulas behind them',
        },
      ]
    : [];

  // Recharts needs numbers, but the API sends display strings ("42.9%").
  const repeatChartData = (repeat?.periods ?? []).map((p) => ({
    label: p.label,
    rate: Number(p.repeat_rate.replace('%', '')) || 0,
    cohort_size: p.cohort_size,
    repeat_purchasers: p.repeat_purchasers,
  }));

  const tinyCohorts = (repeat?.periods ?? []).filter((p) => p.cohort_size < SMALL_COHORT).length;
  const totalCohort = (repeat?.periods ?? []).reduce((s, p) => s + p.cohort_size, 0);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Analytics</h1>
          <p className="text-sm text-sub">How customers move from signup to a second purchase</p>
        </div>
        <button
          className="btn-outline"
          onClick={() => {
            loadFunnel();
            loadRepeat();
            loadStage();
            loadOtp();
          }}
          disabled={loading}
        >
          <IconRefresh className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ══ Section 1 — user journey funnel ══════════════════════════════════ */}
      <section className="space-y-4">
        <SectionHeading
          title="1. User journey"
          blurb="Take everyone who signed up in this period and follow what they did next. Each bar is a smaller group than the one above it."
        >
          <RangePicker
            presets={FUNNEL_PRESETS}
            preset={funnelPreset}
            from={customFrom}
            to={customTo}
            onPreset={setFunnelPreset}
            onFrom={setCustomFrom}
            onTo={setCustomTo}
          />
          <Segmented
            value={granularity}
            onChange={setGranularity}
            options={[
              { value: 'day', label: 'Day' },
              { value: 'week', label: 'Week' },
              { value: 'month', label: 'Month' },
            ]}
          />
        </SectionHeading>

        {funnelError && <ErrorBanner message={funnelError} onRetry={loadFunnel} />}

        {funnelLoading && !funnel ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-96" />
            <Skeleton className="h-96" />
          </div>
        ) : funnel && f ? (
          <>
            {/* Plain-English restatement of the whole funnel, so nobody has to
                assemble the story from four separate bars. */}
            {f.total_signups > 0 && (
              <div className="card p-5 text-sm leading-relaxed">
                <span className="text-sub">Between </span>
                <span className="font-semibold">{formatDate(funnel.period.from)}</span>
                <span className="text-sub"> and </span>
                <span className="font-semibold">{formatDate(funnel.period.to)}</span>
                <span className="text-sub">, </span>
                <span className="font-semibold" style={{ color: stageColors.signups }}>
                  {plural(f.total_signups, 'person', 'people')}
                </span>
                <span className="text-sub"> signed up. Of those, </span>
                <span className="font-semibold" style={{ color: stageColors.carts }}>
                  {f.added_to_cart}
                </span>
                <span className="text-sub"> added something to their bag, </span>
                <span className="font-semibold" style={{ color: stageColors.orders }}>
                  {f.placed_order}
                </span>
                <span className="text-sub"> went on to buy, and </span>
                <span className="font-semibold" style={{ color: stageColors.repeat }}>
                  {f.repeat_orderers}
                </span>
                <span className="text-sub"> came back for a second order.</span>
              </div>
            )}

            {f.total_signups > 0 && f.total_signups < SMALL_SAMPLE && (
              <Caution>
                Only {plural(f.total_signups, 'person', 'people')} signed up in this period, so the
                percentages below will swing hugely — one extra customer can move them by 20 points
                or more. Pick a longer date range before drawing any conclusion from them.
              </Caution>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              <Card title="Where people drop off">
                {f.total_signups === 0 ? (
                  <EmptyState
                    message="No signups in this period"
                    hint="Pick a longer date range"
                  />
                ) : (
                  <>
                    <FunnelStages stages={stages} selected={stage} onSelect={selectStage} />
                    <div className="mt-5 space-y-2 border-t border-edge pt-3 text-xs text-sub">
                      <p>
                        <span className="font-semibold text-ink">Reading the percentages:</span> the
                        one between two bars compares that bar to the one directly above it. The one
                        in brackets compares it to the total who signed up.
                      </p>
                      <p>
                        <span className="font-semibold text-ink">Only the first bar is
                        date-filtered.</span>{' '}
                        The rest ask "has this person ever done it?", at any time since. A recent
                        period always looks weaker because those people have had less time.
                      </p>
                      <p>
                        <span className="font-semibold text-ink">"Added to cart" includes
                        buyers.</span>{' '}
                        A bag is emptied once payment succeeds, so counting live carts alone would
                        miss every customer who actually completed a purchase.
                      </p>
                    </div>
                  </>
                )}
              </Card>

              <Card title="Activity over time">
                <div className="h-72">
                  {funnel.trend.length === 0 ? (
                    <EmptyState message="No activity in this period" />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      {/*
                        Grouped bars, not lines. These are counts of things that
                        happened on separate days — a line implies the value
                        drifted continuously between two points, and with sparse
                        data (one order on Tuesday, none until Friday) that reads
                        as a smooth swell that never happened. Bars also stop the
                        three series from hiding each other when they coincide,
                        which a line chart cannot do at all.
                      */}
                      <BarChart data={funnel.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="period"
                          tickFormatter={(v: string) => periodLabel(v, granularity)}
                          tick={{ fill: t.axis, fontSize: 11 }}
                          stroke={t.grid}
                          tickLine={false}
                          minTickGap={16}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fill: t.axis, fontSize: 11 }}
                          stroke={t.grid}
                          tickLine={false}
                          axisLine={false}
                          width={36}
                        />
                        <Tooltip
                          contentStyle={t.tooltip}
                          labelFormatter={(l: any) => periodLabel(String(l), granularity, true)}
                          cursor={{ fill: t.grid, opacity: 0.4 }}
                        />
                        <Legend
                          formatter={(value) => (
                            <span style={{ color: t.axis, fontSize: 12 }}>{value}</span>
                          )}
                          iconType="square"
                          iconSize={9}
                        />
                        <Bar
                          dataKey="signups"
                          name="New signups"
                          fill={stageColors.signups}
                          radius={[3, 3, 0, 0]}
                          maxBarSize={16}
                        />
                        <Bar
                          dataKey="carts"
                          name="Bags filled"
                          fill={stageColors.carts}
                          radius={[3, 3, 0, 0]}
                          maxBarSize={16}
                        />
                        <Bar
                          dataKey="orders"
                          name="Orders placed"
                          fill={stageColors.orders}
                          radius={[3, 3, 0, 0]}
                          maxBarSize={16}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <p className="mt-3 border-t border-edge pt-3 text-xs text-sub">
                  Counts of what happened in each {granularity}, independent of the funnel above —
                  an order here may belong to someone who signed up long before this period.
                  "Bags filled" counts people who added something and have not checked out yet, so
                  it stays low when everyone who adds also buys.
                </p>
              </Card>
            </div>

            {stage && (
              <StageUsers
                stage={stage}
                data={stageUsers}
                loading={stageLoading}
                error={stageError}
                onPage={setStagePage}
                onClose={() => setStage(null)}
                onRetry={loadStage}
              />
            )}
          </>
        ) : null}
      </section>

      {/* ══ Section 2 — formulation funnel ═══════════════════════════════════ */}
      {/* Shares the date range and loading state of section 1 — it arrives on
          the same request, and two independent range pickers pointed at the
          same window would only invite them to disagree. */}
      <section className="space-y-4">
        <SectionHeading
          title="2. What happens after someone sees their formula?"
          blurb="Every formula generated in this period, and how far it got. This is where you find out whether people are dropping off at the reveal or at the checkout."
        >
          <span className="text-xs text-sub">
            Same date range as above
          </span>
        </SectionHeading>

        {funnelLoading && !funnel ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-80" />
            <Skeleton className="h-80" />
          </div>
        ) : ff ? (
          <>
            {ff.total_formulations_generated > 0 && (
              <div className="card p-5 text-sm leading-relaxed">
                <span className="font-semibold" style={{ color: formulationColors.generated }}>
                  {plural(ff.total_formulations_generated, 'formula', 'formulas')}
                </span>
                <span className="text-sub"> generated between </span>
                <span className="font-semibold">{formatDate(funnel!.period.from)}</span>
                <span className="text-sub"> and </span>
                <span className="font-semibold">{formatDate(funnel!.period.to)}</span>
                <span className="text-sub">. Of those, </span>
                <span className="font-semibold" style={{ color: formulationColors.carted }}>
                  {ff.added_to_cart}
                </span>
                <span className="text-sub"> went into a bag and </span>
                <span className="font-semibold" style={{ color: formulationColors.ordered }}>
                  {ff.placed_order}
                </span>
                <span className="text-sub">
                  {' '}
                  {ff.placed_order === 1 ? 'was' : 'were'} bought — a{' '}
                </span>
                <span className="font-semibold">{ff.conversion_rate}</span>
                <span className="text-sub"> conversion.</span>
              </div>
            )}

            {ff.total_formulations_generated > 0 &&
              ff.total_formulations_generated < SMALL_SAMPLE && (
                <Caution>
                  Only {plural(ff.total_formulations_generated, 'formula', 'formulas')} in this
                  period, so these percentages will swing wildly — one more customer can move them
                  by tens of points. Widen the date range before acting on them.
                </Caution>
              )}

            <div className="grid gap-4 lg:grid-cols-2">
              <Card title="Where formulas are lost">
                {ff.total_formulations_generated === 0 ? (
                  <EmptyState
                    message="No formulas generated in this period"
                    hint="Pick a longer date range"
                  />
                ) : (
                  <>
                    <FormulationFunnelStages stages={formulationStages} />
                    <div className="mt-5 space-y-2 border-t border-edge pt-3 text-xs text-sub">
                      <p>
                        <span className="font-semibold text-ink">This counts formulas, not
                        people.</span>{' '}
                        One customer who generated three formulas is three rows at the top.
                      </p>
                      <p>
                        <span className="font-semibold text-ink">Only the first bar is
                        date-filtered.</span>{' '}
                        The other two ask "did this formula ever get that far?", at any time since.
                        A recent period always looks weaker because those formulas have had less
                        time.
                      </p>
                      <p>
                        <span className="font-semibold text-ink">"Added to cart" includes
                        buyers.</span>{' '}
                        A bag is emptied once payment succeeds, so counting live carts alone would
                        miss every formula that was actually bought.
                      </p>
                    </div>
                  </>
                )}
              </Card>

              <Card title="The two drop-offs">
                {ff.total_formulations_generated === 0 ? (
                  <EmptyState message="Nothing to compare yet" />
                ) : (
                  <div className="space-y-4">
                    {/* The whole point of this section: which of the two gaps is
                        costing more, stated as a number rather than left to be
                        eyeballed off the bars. */}
                    <div className="rounded-xl border border-edge p-4">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-sm font-medium">Saw the formula, never bagged it</span>
                        <span className="text-2xl font-semibold tabular-nums text-warn">
                          {ff.drop_off_at_cart.toLocaleString('en-IN')}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-sub">
                        The reveal did not convince them. Look at the result screen: the price, the
                        notes, what the formula actually says about them.
                      </p>
                    </div>

                    <div className="rounded-xl border border-edge p-4">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-sm font-medium">Bagged it, never paid</span>
                        <span className="text-2xl font-semibold tabular-nums text-warn">
                          {ff.drop_off_at_checkout.toLocaleString('en-IN')}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-sub">
                        They wanted it. Something in checkout stopped them — shipping cost, the
                        payment step, or a form that asked too much.
                      </p>
                    </div>

                    <p className="border-t border-edge pt-3 text-xs text-sub">
                      {ff.drop_off_at_cart === 0 && ff.drop_off_at_checkout === 0
                        ? 'Every formula generated in this period was bought.'
                        : ff.drop_off_at_cart >= ff.drop_off_at_checkout
                          ? 'More is being lost at the reveal than at checkout — the formula itself is where to look first.'
                          : 'More is being lost at checkout than at the reveal — people want the perfume; something in the buying flow is in the way.'}
                    </p>
                  </div>
                )}
              </Card>
            </div>
          </>
        ) : null}

        {showCartUsers && <CartUsersModal onClose={() => setShowCartUsers(false)} />}
        {showGenerators && <FormulationUsersModal onClose={() => setShowGenerators(false)} />}
        {showConverted && <ConvertedOrdersModal onClose={() => setShowConverted(false)} />}
      </section>

      {/* ══ Section 3 — repeat purchase rate ═════════════════════════════════ */}
      <section className="space-y-4">
        <SectionHeading
          title="3. Do first-time buyers come back?"
          blurb="Customers are grouped by when they placed their very first order. The rate is how many of that group have since bought again."
        >
          <RangePicker
            presets={COHORT_PRESETS}
            preset={repeatPreset}
            from={repeatCustomFrom}
            to={repeatCustomTo}
            onPreset={setRepeatPreset}
            onFrom={setRepeatCustomFrom}
            onTo={setRepeatCustomTo}
          />
          <Segmented
            value={cohortPeriod}
            onChange={setCohortPeriod}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'quarterly', label: 'Quarterly' },
            ]}
          />
        </SectionHeading>

        {repeatError && <ErrorBanner message={repeatError} onRetry={loadRepeat} />}

        {repeatLoading && !repeat ? (
          <div className="space-y-4">
            <Skeleton className="h-28" />
            <Skeleton className="h-72" />
          </div>
        ) : repeat ? (
          <div className="space-y-4">
            <div className="card flex flex-wrap items-end justify-between gap-4 p-5">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-sub">
                  Overall repeat rate
                </p>
                <p className="mt-2 text-3xl font-semibold text-gold">{repeat.overall_repeat_rate}</p>
                <p className="mt-1 text-xs text-sub">
                  based on {plural(totalCohort, 'first-time buyer')}
                </p>
              </div>
              <p className="max-w-md text-xs text-sub">
                Out of everyone who placed their first order in this range, this is the share who
                have since placed a second one. The second order counts whenever it happened, so
                the most recent groups will always look weakest — they have had the least time.
              </p>
            </div>

            {totalCohort > 0 && tinyCohorts > 0 && (
              <Caution>
                {tinyCohorts === 1
                  ? 'One group below has fewer than 5 customers in it.'
                  : `${tinyCohorts} of the groups below have fewer than 5 customers in them.`}{' '}
                A rate built on one or two people is not a trend — "100%" can mean a single customer
                who happened to order twice. Check the customer count on each bar before quoting a
                percentage.
              </Caution>
            )}

            <Card title={`Repeat rate by ${cohortPeriod === 'monthly' ? 'month' : 'quarter'} of first order`}>
              <div className="h-72">
                {repeatChartData.length === 0 ? (
                  <EmptyState
                    message="No first orders in this range"
                    hint="Pick a longer date range"
                  />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={repeatChartData} margin={{ top: 24, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: t.axis, fontSize: 11 }}
                        stroke={t.grid}
                        tickLine={false}
                        minTickGap={8}
                      />
                      <YAxis
                        unit="%"
                        domain={[0, 100]}
                        tick={{ fill: t.axis, fontSize: 11 }}
                        stroke={t.grid}
                        tickLine={false}
                        axisLine={false}
                        width={44}
                      />
                      <Tooltip
                        contentStyle={t.tooltip}
                        cursor={{ fill: t.grid, opacity: 0.4 }}
                        formatter={(value: any, _name: any, entry: any) => [
                          `${entry.payload.repeat_purchasers} of ${plural(
                            entry.payload.cohort_size,
                            'customer',
                          )} came back (${Number(value).toFixed(1)}%)`,
                          'Repeat rate',
                        ]}
                      />
                      {/*
                        A single customer who happened to buy twice renders as a
                        confident "100%". Printing the group size above every bar
                        — and greying out the ones too small to mean anything —
                        keeps the sample in view instead of buried in a tooltip.
                      */}
                      <Bar dataKey="rate" radius={[4, 4, 0, 0]} maxBarSize={48}>
                        {repeatChartData.map((d, i) => (
                          <Cell
                            key={i}
                            fill={t.gold}
                            fillOpacity={d.cohort_size < SMALL_COHORT ? 0.4 : 1}
                          />
                        ))}
                        <LabelList
                          dataKey="cohort_size"
                          position="top"
                          formatter={(v: any) => plural(Number(v), 'customer')}
                          fill={t.axis}
                          fontSize={10}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
              <p className="mt-3 border-t border-edge pt-3 text-xs text-sub">
                The label above each bar is how many customers are in that group. Faded bars have
                fewer than {SMALL_COHORT} customers, so their percentage is not meaningful.
              </p>
            </Card>

            <Card title="Group breakdown" bodyClassName="!p-0">
              {repeat.periods.length === 0 ? (
                <EmptyState message="No groups in this range" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-edge">
                        <th className="th">First ordered in</th>
                        <th className="th">Customers</th>
                        <th className="th">Bought again</th>
                        <th className="th">Repeat rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-edge">
                      {repeat.periods.map((p) => {
                        const tiny = p.cohort_size < SMALL_COHORT;
                        return (
                          <tr key={p.period} className="transition-colors hover:bg-edge/20">
                            <td className="td font-medium">{p.label}</td>
                            <td className="td tabular-nums text-sub">{p.cohort_size}</td>
                            <td className="td tabular-nums text-sub">{p.repeat_purchasers}</td>
                            <td className="td">
                              <span className={tiny ? 'text-sub' : 'font-semibold text-gold'}>
                                {p.repeat_rate}
                              </span>
                              {tiny && (
                                <span className="ml-2 text-xs text-sub/70">too few to judge</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        ) : null}
      </section>

      {/* ══ Section 4 — SMS OTP login health ═════════════════════════════════ */}
      {/* Hidden entirely when the fetch failed: this supplements the page, and
          an auth service that is briefly down should not put an error banner
          over analytics that loaded perfectly well. */}
      {!otpFailed && (
        <section className="space-y-4">
          <SectionHeading
            title="4. OTP login"
            blurb="Codes texted to customers signing in by mobile number, and how many were actually entered. A verify rate falling off is the first sign SMS delivery is in trouble."
          >
            <span className="text-xs text-sub">Last 30 days</span>
          </SectionHeading>

          {otpLoading && !otp ? (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Skeleton className="h-20" />
                <Skeleton className="h-20" />
                <Skeleton className="h-20" />
                <Skeleton className="h-20" />
              </div>
              <Skeleton className="h-56" />
            </div>
          ) : otp ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <OtpStat label="Codes sent" value={otp.summary.total_sent} />
                <OtpStat
                  label="Verified"
                  value={otp.summary.total_verified}
                  tone="text-ok"
                />
                <OtpStat
                  label="Verify rate"
                  value={`${otp.summary.success_rate}%`}
                  tone={otpRateTone(otp.summary.success_rate)}
                />
                <OtpStat label="Unique numbers" value={otp.summary.unique_phones} />
              </div>

              <Card title="Sent vs verified">
                <div className="h-[180px]">
                  {otpChartData.length === 0 ? (
                    <EmptyState
                      message="No OTP logins yet"
                      hint="Nothing has been texted in the retained window"
                    />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={otpChartData}
                        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fill: t.axis, fontSize: 11 }}
                          stroke={t.grid}
                          tickLine={false}
                          minTickGap={16}
                        />
                        <YAxis
                          allowDecimals={false}
                          tick={{ fill: t.axis, fontSize: 11 }}
                          stroke={t.grid}
                          tickLine={false}
                          axisLine={false}
                          width={32}
                        />
                        <Tooltip contentStyle={t.tooltip} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {/* Gold over green is the pair used everywhere else on
                            this page; the gap between the lines IS the story. */}
                        <Line
                          type="monotone"
                          dataKey="sent"
                          name="Sent"
                          stroke={t.gold}
                          strokeWidth={2}
                          dot={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="verified"
                          name="Verified"
                          stroke={t.status.delivered}
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>

                <div className="mt-3 space-y-1 border-t border-edge pt-3 text-xs text-sub">
                  <p>
                    Expired without verification:{' '}
                    <span className="tabular-nums">{otp.summary.total_expired}</span>
                  </p>
                  <p>
                    Failed (too many attempts):{' '}
                    <span className="tabular-nums">{otp.summary.total_failed}</span>
                  </p>
                  <p className="pt-1 text-sub/70">
                    Spent codes are deleted an hour after they stop being usable, so these
                    counts describe recent activity rather than a full 30-day history.
                  </p>
                </div>
              </Card>
            </>
          ) : null}
        </section>
      )}
    </div>
  );
}
